import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { map, Observable, ReplaySubject, startWith, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import {
  TableCellOnActionEvent,
  TableConstructorData,
  UserTableConstructorInfo,
} from 'src/app/interfaces/tables.interfaces';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { CommunityFocus, PaginatablePendingUserInvolvements, RequestCountersBasic } from 'src/app/swagger-api';
import { SocialInvolvementContext } from '../../shared/counters/involvement-counters-context.abstract';
import { initialPaginationState } from '../../shared/social-initiative/social-initiative.helpers';
import { CellActionTypes } from '../../tables/components/cell-actions.type';
import { filterDataForUserTable } from '../../tables/table.helpers';
import { actionButtonTestId } from '../../user-relation-requests/components/manage-relation-request/manage-relation-request.helper';
import { CommunityMapper, CommunityView } from '../communities-store';
import { CommunityFormState, FieldState } from '../community-form-state';

import { Router } from '@angular/router';
import { CommunityFormData } from '../create-community.interfaces';
import { ManageCommunityService } from '../manage-community.service';

export enum CommunityUpdateTabType {
  UPDATE_COMMUNITY_TAB = 'update_data',
  USER_REQUESTS_JOIN = 'user_requests',
  PENDINGS_INVITATIONS = 'sent_invitations',
}

@Component({
  selector: 'app-manage-community',
  templateUrl: './manage-community.component.html',
  styleUrls: ['./manage-community.component.scss'],
})
export class ManageCommunityComponent extends SocialInvolvementContext implements OnInit, IDeactivate {
  @Input({ required: true }) community: CommunityView;

  public currentTab = CommunityUpdateTabType.UPDATE_COMMUNITY_TAB;

  private formValueChanges$: ReplaySubject<CommunityFormData> = new ReplaySubject();
  private subscriptions$: Subscription[] = [];

  private tabTypes: { [key in CommunityUpdateTabType]: () => void } = {
    update_data: () => {
      this.manageCommunityService.fetchAttributeMetadata(this.community);
    },

    user_requests: () =>
      this.manageCommunityService.fetchUsersRequestsForEntity(
        this.community.entity_id,
        this.calculateOffset(this.resolver),
        this.pageSize,
        this.sorting,
        this.community.permissions.accept_join_request,
      ),
    sent_invitations: () =>
      this.manageCommunityService.fetchUsersInvitationsForEntity(
        this.community.entity_id,
        this.calculateOffset(this.resolver),
        this.pageSize,
        this.sorting,
        this.community.permissions.invite_users,
      ),
  };

  private router = inject(Router);
  private sessionService = inject(SessionService);
  private manageCommunityService = inject(ManageCommunityService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  public readonly LINK_COMMUNITY_VIEW = this.manageCommunityService.SOCIAL_ENTITY_ROUTE_LINK;

  private formState: CommunityFormState = new CommunityFormState(
    this.formValueChanges$,
    this.manageCommunityService.onFormAttrChanges,
    this.sessionService.sessionChanges.pipe(
      map(({ userType }) => userType),
      startWith(this.sessionService.userType),
    ),
  );

  public readonly formContent$: Observable<FormConstructorContent> = this.formState.formContent$;
  public readonly focusState$: Observable<FieldState<CommunityFocus>> = this.formState.focusState$;

  public readonly tableData$: Observable<UserTableConstructorInfo> = this.manageCommunityService.tableDataChanged.pipe(
    map((data: TableConstructorData) => filterDataForUserTable(data)),
  );

  public ngOnInit(): void {
    this.subscribeOnCounterChanges();
    this.subscribeOnResolverChanges();
    this.initializeResolvers();

    this.manageCommunityService.fetchAttributeMetadata(this.community);
    this.manageCommunityService.fetchRequestCounters(this.community.entity_id);
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public setFormStatus(dirty: boolean): void {
    this.isFormChanged = dirty;
  }

  public onFormValueChanged(formModel: CommunityFormData) {
    this.formValueChanges$.next(formModel);
  }

  public onTabClick($event: NgbNavChangeEvent<CommunityUpdateTabType>): void {
    this.resetSortingParameters();
    this.resetPaginationControls();
    this.manageCommunityService.fetchRequestCounters(this.community.entity_id);

    if (this.tabTypes[$event.nextId]) {
      this.currentTab = $event.nextId;
      this.tabTypes[this.currentTab]();
    }
  }

  public onTableAction(event: TableCellOnActionEvent): void {
    const actions: { [key: string]: () => void } = {
      [CellActionTypes.CONFIRM]: () => this.onConfirmRequestOrInvite(event),
      [CellActionTypes.REJECT]: () => this.onRejectRequestOrInvite(event),
      [CellActionTypes.REMOVE]: () => this.onRemoveInvite(event),
    };
    actions[event.type] && actions[event.type]();
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        successfulMessage: 'MANAGE_COMMUNITY_CONFIRM_USER_REQUEST_HINT',
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.REJECT, event) },
        ],
      },
      () => {
        this.manageCommunityService.onConfirmRequestOrInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        ],
        successfulMessage: 'MANAGE_COMMUNITY_REJECT_USER_REQUEST_HINT',
        confirmationDialog: {
          description: 'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_DESCRIPTION',
          title: 'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageCommunityService.onRejectRequestOrInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public onRemoveInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'MANAGE_ENTITY_REMOVE_USER_REQUEST_HINT',
        confirmationDialog: {
          description: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_DESCRIPTION',
          title: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageCommunityService.onRemoveInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public removeCommunity(): void {
    this.operationInfoService.setupOperation(
      {
        successfulMessage: 'MANAGE_COMMUNITY_REMOVE_HINT',
        confirmationDialog: {
          description: 'REMOVE_COMMUNITY_DIALOG_DESCRIPTION',
          title: 'BTN_REMOVE_COMMUNITY_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'REMOVE_COMMUNITY_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageCommunityService.deleteCommunity(this.community.entity_id);
      },
    );
  }

  public updateCommunity(formData: CommunityFormData): void {
    const updateRequest = CommunityMapper.isCommunityEntity(formData)
      ? CommunityMapper.toCommunityCreateRequest(formData)
      : CommunityMapper.toCharityCreateRequest(formData);

    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'btn-update-community' },
        successfulMessage: CommunityMapper.isCommunityEntity
          ? 'UPDATE_COMMUNITY_DESCRIPTION'
          : 'UPDATE_CHARITY_DESCRIPTION',
      },
      () => {
        this.manageCommunityService.updateEntity(updateRequest, this.community.entity_id);
      },
    );
    this.isFormChanged = false;
  }

  public onCancel() {
    promiseWithCatch(this.router.navigateByUrl(`${this.LINK_COMMUNITY_VIEW}/${this.community.entity_id}`));
  }

  protected subscribeOnCounterChanges(): void {
    this.subscriptions$.push(
      this.manageCommunityService.communityInvolvementCountersChanges.subscribe((counters: RequestCountersBasic) => {
        this.receivedCounter = counters.requests_received;
        this.sentCounter = counters.requests_sent;
      }),
    );
  }

  protected onListUpdate(): void {
    if (this.resolver.state.pagination.itemsCount === 1 && this.resolver.state.pagination.currentPage > 1) {
      this.resolver.state.pagination.currentPage -= 1;
    }

    this.manageCommunityService.fetchRequestCounters(this.community.entity_id);
    this.tabTypes[this.currentTab]();
  }

  private subscribeOnResolverChanges(): void {
    this.subscriptions$.push(
      this.manageCommunityService.resolverChanged.subscribe((data: PaginatablePendingUserInvolvements) => {
        this.resolver.state.pagination.totalItemsCount = data.total_count;
        this.resolver.state.pagination.itemsCount = data.items.length;
      }),
    );
  }

  private resetPaginationControls(): void {
    this.resolver.state = initialPaginationState();
  }

  private calculateOffset(resolver: PaginationResolver): number {
    return (resolver.state.pagination.currentPage - 1) * this.pageSize;
  }

  private initializeResolvers(): void {
    const onPageChangesFactory = (resolver: PaginationResolver) => (page: number) => {
      resolver.state.pagination.currentPage = page;
      this.tabTypes[this.currentTab]();
    };

    this.resolver.onPageChanged = onPageChangesFactory(this.resolver);
  }
}
